<template>
  <div class="personal-log">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column is-full">
            <div class="box">
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-full">
                  <b-field required :label="$t('personal-log-view.select-date-title')">
                    {{ getFormattedDate(selected_date) }}
                  </b-field>
                  <b-field required :label="$t('personal-log-view.personal')">
                    <b-taginput
                        v-if="isInNewMode"
                        maxtags="1"
                        v-model="personnelTags"
                        :data="filteredPersonnel"
                        :loading="false"
                        :has-counter="false"
                        :open-on-focus="true"
                        autocomplete
                        field="fullname"
                        icon="account-box"
                        :remove-on-keys=[]
                        :placeholder="$t('personal-log-view.personal-placeholder')"
                        tabindex="2"
                        @typing="getFilteredPersonnel">
                    </b-taginput>
                    <label v-else>{{ personalFullName }}</label>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column is-half">
                  <b-field required :label="$t('personal-log-view.hours')">
                    <b-numberinput :editable="true" tabindex="3" v-model="personalWorkingHour" min="0" step="1" max="24">
                    </b-numberinput>
                  </b-field>
                </div>
                <div class="column is-half">
                  <b-field required :label="$t('personal-log-view.minutes')">
                    <b-numberinput :editable="false" tabindex="4" v-model="personalWorkingMinutes" min="0" max="45"
                                    step="15">
                    </b-numberinput>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column is-full">
                  <b-field :label="$t('personal-log-view.comments')">
                    <b-input v-model="text"
                              :placeholder="$t('personal-log-view.comments-placeholder')" tabindex="5"
                              type="textarea"></b-input>
                  </b-field>
                  <label v-if="!isFormValid"
                          class="required-field">{{ $t('validation.personal-log-form') }}
                  </label>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column is-narrow">
                  <b-button tabindex="10" class="button" @click="cancelClicked" icon-left="close-thick">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button tabindex="11" class="button is-primary" @click="saveClicked"
                            icon-left="content-save">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "PersonalWorklog",
  props: {
    'project_id': {
      type: String,
    },
    'log_id': {
      type: String,
    },
    'log_data': {},
    'personnel_data': {},
    'selected_date': {}
  },
  data() {
    return {
      personnelTags: [],
      filteredPersonnel: this.personnel_data,
      personalFullName: "",
      personalWorkingHour: 0,
      personalWorkingMinutes: 0,
      text: "",
      isLoading: false,
      isFormValid: true
    }
  },
  methods: {
    createPersonalWorklog: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertPersonalWorklogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertPersonalWorklogFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create personal worklog result: ", result.data);
        this.showSuccess(this.$t('messages.personal-log-created'));
        this.$emit('personalWorklogAdded');
      }).catch((error) => {
        this.showError(error.message)
        this.isLoading = false
        console.log("Error writing document: ", error);
      });
    },
    updatePersonalWorklog: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const updatePersonalWorklogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_log";
      updatePersonalWorklogFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("update personal worklog result: ", result.data);
        this.showSuccess(this.$t('messages.personal-log-updated'));
        this.$emit('personalWorklogUpdated');
      }).catch((error) => {
        this.isLoading = false
        this.showError(error.message)
        console.log("Error writing document: ", error);
      });
    },
    getFilteredPersonnel(text) {
      this.filteredPersonnel = this.personnel_data.filter((option) => {
        return option.fullname
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    validateForm() {
      return this.selected_date != null 
          && ((this.isInNewMode && this.personnelTags != null && this.personnelTags.length > 0) || (!this.isInNewMode && this.personalFullName != ""))
    },
    /*
     UI Events
     */
    cancelClicked: function () {
      this.$emit('close');
    },
    backClicked: function () {
      this.$router.back();
    },
    saveClicked: function () {
      this.isFormValid = true
      if (!this.validateForm()) {
        this.isFormValid = false
        return
      }
      // setting current time to the selected date
      const currentDate = new Date()
      this.selected_date.setHours(currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds(), currentDate.getMilliseconds())
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        quantity: (this.personalWorkingHour + (this.personalWorkingMinutes / 60)),
        text: this.text
      }
      if (this.isInNewMode){
        dataFieldsToUpdate.date = this.selected_date.toISOString();
        dataFieldsToUpdate.personal_id = this.personnelTags[0].personal.document_id;
        console.log("creating new personal worklog: ", dataFieldsToUpdate);
        this.createPersonalWorklog(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.personal_id = this.log_data.personal_id;
        dataFieldsToUpdate.document_id = this.log_data.document_id;
        console.log("updating new personal worklog: ", dataFieldsToUpdate);
        this.updatePersonalWorklog(dataFieldsToUpdate);
      }
    }
  },
  mounted() {
    this.logData = {};
    if (this.isInNewMode && this.currentUserAccess("ProjectLogs", this.UserActions.CREATE, this.project_id)) {
      console.log("add new personal worklog");
    } else if (!this.isInNewMode && this.currentUserAccess("ProjectLogs", this.UserActions.UPDATE, this.project_id)){
      console.log("update personal worklog");
      this.personalFullName = this.log_data.parent.name.first + " " + this.log_data.parent.name.last;
      this.personalWorkingHour  = Math.floor(this.log_data.quantity)
      this.personalWorkingMinutes = (this.log_data.quantity % 1).toFixed(2) * 60;
      this.text = this.log_data.text; 
    } else {
      console.log("component access denied.");
      this.$emit('close');
    }
  },
  computed: {
    isInNewMode() {
      return this.log_data.document_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('personal-log-view.new-log-title')
      } else {
        return this.$t('personal-log-view.edit-log-title')
      }
    }
  }
};
</script>

<style scoped>

.required-field {
  color: red;
  font-size: 10pt;
}
</style>
